import { type ReactElement, type ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import {
  Pie,
  PieChart as BasePieChart,
  Sector,
  Tooltip,
  type PieLabelRenderProps,
  type PieSectorShapeProps,
  type TooltipContentProps,
} from 'recharts';
import { formatCount, formatPercent } from '../../../utils/format';
import { TooltipContent } from './Tooltip';

type PieDataItem = {
  label: string;
  value: number;
  color?: string;
};

type AngledPieDataItem = PieDataItem & {
  percentage: number;
  startAngle: number;
  endAngle: number;
};

type Props = {
  data: PieDataItem[];
  unit?: string;
  showsLabel?: boolean;
  sorted?: boolean;
};

const RADIAN = Math.PI / 180;

const PieChart = ({
  data,
  unit,
  showsLabel = true,
  sorted = true,
}: Props): ReactNode => {
  const pieData = useMemo((): AngledPieDataItem[] => {
    // 1位から順番に薄くなるような色の配列を作成する
    const colors = data.map((_, index: number): string => {
      const [min, max] = [0.3, 1.0];
      const opacity = 1.0 - (index / data.length) * (max - min);
      return `#023894${Math.round(opacity * 255)
        .toString(16)
        .padStart(2, '0')}`;
    });

    const sum = data.reduce((sum: number, item): number => {
      return sum + item.value;
    }, 0);

    // 並び替えのオプションが指定された場合は値の大きい順にソートする
    const sortedData = sorted
      ? data.toSorted((item1: PieDataItem, item2: PieDataItem): number => {
          return item2.value - item1.value;
        })
      : data;

    // 円グラフ用に各データに角度とパーセンテージの情報を追加する
    return sortedData.reduce(
      (
        accumulator: { pieData: AngledPieDataItem[]; currentAngle: number },
        item: PieDataItem,
        index: number
      ): { pieData: AngledPieDataItem[]; currentAngle: number } => {
        const startAngle = accumulator.currentAngle;
        const endAngle = startAngle + (item.value / sum) * 360;
        return {
          pieData: [
            ...accumulator.pieData,
            {
              ...item,
              color: item.color || colors[index],
              startAngle: startAngle,
              endAngle: endAngle,
              percentage: (item.value / sum) * 100,
            },
          ],
          currentAngle: endAngle,
        };
      },
      { pieData: [], currentAngle: -90 }
    ).pieData;
  }, [data, sorted]);

  return (
    <StyledPieChart responsive reverseStackOrder>
      <Tooltip
        content={({ active, payload }: TooltipContentProps): ReactNode => {
          if (active && payload && payload.length > 0) {
            const data: AngledPieDataItem = payload[0].payload;
            return (
              <TooltipContent
                label={data.label}
                values={[
                  {
                    fill: data.color || '#000000',
                    value: `${formatCount(data.value)}${
                      unit ? ` ${unit}` : ''
                    } (${formatPercent(data.percentage)})`,
                  },
                ]}
              />
            );
          }
          return null;
        }}
      />
      <Pie
        data={pieData}
        labelLine={showsLabel}
        label={showsLabel ? PieLabel : undefined}
        dataKey="value"
        startAngle={90}
        endAngle={-270}
        isAnimationActive={false}
        shape={(props: PieSectorShapeProps): ReactElement => {
          const item: AngledPieDataItem | undefined = props.payload;
          return (
            <Sector {...props} fill={item?.color ?? props.fill} stroke="none" />
          );
        }}
      />
    </StyledPieChart>
  );
};

const PieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  payload,
}: PieLabelRenderProps): ReactNode => {
  if (cx == null || cy == null || innerRadius == null || outerRadius == null) {
    return null;
  }
  const radius = innerRadius + (outerRadius - innerRadius) + 20;
  const ncx = Number(cx);
  const x = ncx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
  const ncy = Number(cy);
  const y = ncy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);
  const w = 100;
  const h = 40;

  return (
    <foreignObject
      x={x > ncx ? x : x - w}
      y={y > ncy ? y : y - h}
      width={w}
      height={h}
      style={{ textAlign: x > ncx ? 'start' : 'end', fontSize: '12px' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: x > ncx ? 'flex-start' : 'flex-end',
        }}
      >
        <div>{payload?.label}</div>
        <div>{`${formatPercent((percent ?? 1) * 100)}`}</div>
      </div>
    </foreignObject>
  );
};

const StyledPieChart = styled(BasePieChart)`
  width: 100%;
  max-height: 300px;
  aspect-ratio: 1.618;
  & > svg {
    overflow: visible;
  }
`;

export type { PieDataItem };
export { PieChart };
